import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/pages/Login";
import { api, formatApiErrorDetail } from "@/lib/api";

const SHAPES = ["Round Brilliant", "Oval", "Cushion", "Emerald", "Princess", "Pear", "Marquise", "Radiant"];
const CUT_OPTIONS = ["Excellent", "Very Good", "Good"];
const COLORS = ["D", "E", "F", "G", "H", "I", "J"];
const CLARITY = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2"];
const LABS = ["GIA", "GCAL by Sarine", "HRD", "IGI"];

export default function SubmitDiamond() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const kind = params.get("kind") === "rough" ? "rough" : "polished";
  const isRough = kind === "rough";
  const [form, setForm] = useState({
    name: "", carat: "", shape: SHAPES[0], color: COLORS[0], clarity: CLARITY[2], cut: "Excellent",
    origin_region: "Jwaneng", rough_reference: "", manufacturer: "",
    manufactured_where: "Botswana", rough_id: "",
    want_pride_mark: false, want_lab_coordination: false, lab_choice: LABS[0], want_diamondeye: false, notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const bundle = 1450;
  const total = bundle + (form.want_pride_mark ? 480 : 0) + (form.want_diamondeye ? 320 : 0);

  let submitLabel = "Submit for review";
  if (loading) submitLabel = "Submitting…";
  else if (isRough) submitLabel = "Register rough diamond";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, kind, carat: parseFloat(form.carat) || 0 };
      const { data } = await api.post("/diamonds", payload);
      toast.success("Diamond submitted for review");
      navigate(`/portal/diamond/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24 mx-auto max-w-4xl px-6 lg:px-10">
      <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-bb-silver/60 hover:text-bb-gold transition-colors duration-300">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.4} /> Back to dashboard
      </Link>
      <h1 className="mt-6 font-serif text-4xl md:text-5xl">{isRough ? "Register a Rough Diamond" : "Submit a Polished Diamond"}</h1>
      <p className="text-sm text-bb-silver/55 mt-2">{isRough
        ? "Register a Botswana rough diamond before manufacturing to receive a unique Botswana Brilliance Rough ID. Records can be updated throughout cutting and polishing."
        : "Provide the polished diamond details to begin the certificate workflow. Documentation review follows submission."}</p>

      <form onSubmit={submit} className="mt-10 space-y-8">
        <div className="glass rounded-2xl p-8 space-y-5">
          <div className="label-eyebrow">Diamond details</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name / reference" testid="submit-name" value={form.name} onChange={set("name")} placeholder="e.g. The Kalahari Light" />
            <Field label="Carat weight" testid="submit-carat" type="number" value={form.carat} onChange={set("carat")} placeholder="2.07" />
            <Select label="Shape" testid="submit-shape" value={form.shape} onChange={set("shape")} options={SHAPES} />
            <Select label="Cut" testid="submit-cut" value={form.cut} onChange={set("cut")} options={CUT_OPTIONS} />
            <Select label="Colour" testid="submit-color" value={form.color} onChange={set("color")} options={COLORS} />
            <Select label="Clarity" testid="submit-clarity" value={form.clarity} onChange={set("clarity")} options={CLARITY} />
            <Field label="Origin region" testid="submit-region" value={form.origin_region} onChange={set("origin_region")} placeholder="Jwaneng" />
            <Field label="Rough parcel reference" testid="submit-rough" value={form.rough_reference} onChange={set("rough_reference")} placeholder="Optional" />
            <Field label="Manufactured in" testid="submit-manufactured-where" value={form.manufactured_where} onChange={set("manufactured_where")} placeholder="e.g. Diamond Park, Gaborone or approved manufacturer" />
            {!isRough && <Field label="Original Rough ID (if known)" testid="submit-rough-id" value={form.rough_id} onChange={set("rough_id")} placeholder="BB-ROUGH-2026-...." />}
          </div>
        </div>

        <div className="glass rounded-2xl p-8 space-y-4">
          <div className="label-eyebrow">Optional services</div>
          <Toggle testid="submit-pride" checked={form.want_pride_mark} onChange={set("want_pride_mark")}
            title="Botswana Pride Mark" desc="Verified, invisible GemEx ION engraving, applied only when authorised." fee="+ $480" />
          <Toggle testid="submit-diamondeye" checked={form.want_diamondeye} onChange={set("want_diamondeye")}
            title="DiamondEye verification product" desc="Separate product to independently verify the physical diamond." fee="+ $320" />
          <Toggle testid="submit-lab" checked={form.want_lab_coordination} onChange={set("want_lab_coordination")}
            title="Laboratory coordination" desc="We arrange independent grading and connect the report. External lab fees apply." fee="Lab fee applies" />
          {form.want_lab_coordination && (
            <Select label="Preferred laboratory" testid="submit-lab-choice" value={form.lab_choice} onChange={set("lab_choice")} options={LABS} />
          )}
        </div>

        <div className="glass rounded-2xl p-8">
          <div className="label-eyebrow mb-3">Notes</div>
          <textarea data-testid="submit-notes" value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3}
            placeholder="Anything our review team should know"
            className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm outline-none focus:border-bb-gold/50 transition-colors duration-300 resize-none" />
        </div>

        <div className="glass rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <div className="label-eyebrow">Estimated Botswana Brilliance fee</div>
            <div className="font-serif text-4xl mt-2">${total.toLocaleString()} <span className="text-base text-bb-silver/50">USD</span></div>
            <p className="text-xs text-bb-silver/45 mt-1">Bundle ${bundle} · excludes external laboratory fees</p>
          </div>
          <button data-testid="submit-diamond-btn" disabled={loading} className="btn-gold rounded-full px-8 py-3.5 text-sm flex items-center gap-2">
            <Check className="h-4 w-4" strokeWidth={1.6} /> {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A1420] px-4 py-3 text-sm outline-none focus:border-bb-gold/50 transition-colors duration-300">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Toggle({ testid, checked, onChange, title, desc, fee }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`w-full text-left rounded-xl border px-5 py-4 flex items-center gap-4 transition-all duration-300 ${
        checked ? "border-bb-gold/50 bg-bb-gold/8" : "border-white/10 bg-white/[0.02] hover:border-bb-gold/25"
      }`}>
      <div className={`grid place-items-center h-6 w-6 rounded-md border shrink-0 transition-colors duration-300 ${checked ? "border-bb-gold bg-bb-gold" : "border-white/25"}`}>
        {checked && <Check className="h-4 w-4 text-black" strokeWidth={2.2} />}
      </div>
      <div className="flex-1">
        <div className="text-sm text-bb-white">{title}</div>
        <div className="text-xs text-bb-silver/55 mt-0.5">{desc}</div>
      </div>
      <span className="text-xs text-bb-gold whitespace-nowrap">{fee}</span>
    </button>
  );
}
